import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, CheckCircle2, TrendingUp } from 'lucide-react';
import styles from './Features.module.css';
import { MODULES } from './featuresData';
import Button from '../../components/Button/Button';
import CTABand from '../../components/CTABand/CTABand';
import Seo, { breadcrumbLd, softwareLd } from '../../components/Seo/Seo';

function Panel({ panel }) {
    return (
        <div className={styles.panel}>
            <div className={styles.panelHead}><span /><span /><span /><strong>{panel.title}</strong></div>
            {panel.rows.map(([a, b, tone]) => (
                <div key={a} className={`${styles.panelRow} ${tone ? styles[tone] : ''}`}><span>{a}</span><b>{b}</b></div>
            ))}
            <small className={styles.sample}>Sample data</small>
        </div>
    );
}

export default function Features() {
    const [active, setActive] = useState(MODULES[0].id);

    // highlight the module in view
    useEffect(() => {
        const io = new IntersectionObserver(
            (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); }),
            { rootMargin: '-45% 0px -50% 0px' }
        );
        MODULES.forEach((m) => { const el = document.getElementById(m.id); if (el) io.observe(el); });
        return () => io.disconnect();
    }, []);

    const jump = (id) => (e) => {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 100, behavior: 'smooth' });
        window.history.replaceState(null, '', `#${id}`);
    };

    return (
        <>
            <Seo
                title="Salon software features: billing, memberships, packages and more"
                description="Explore every Hirsta module: GST billing, prepaid cards, discount memberships, service packages, appointments, client khata, staff payroll, analytics and WhatsApp marketing for salons."
                jsonLd={[softwareLd(), breadcrumbLd([['Home', '/'], ['Features', '/features']])]}
            />
            <header className={styles.header}>
                <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    Everything Hirsta does, module by module
                </motion.h1>
                <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.08 }}>
                    Ten modules that share one database, so a single bill keeps billing, loyalty, staff credit and reports in step.
                </motion.p>
            </header>

            <div className={styles.layout}>
                <nav className={styles.side} aria-label="Modules">
                    {MODULES.map((m) => {
                        const Icon = m.icon;
                        return (
                            <a key={m.id} href={`#${m.id}`} onClick={jump(m.id)} className={`${styles.sideLink} ${active === m.id ? styles.sideActive : ''}`} aria-current={active === m.id ? 'true' : undefined}>
                                {active === m.id && <motion.span layoutId="side-pill" className={styles.sidePill} transition={{ type: 'spring', stiffness: 400, damping: 34 }} />}
                                <Icon size={15} /> <span>{m.name}</span>
                            </a>
                        );
                    })}
                </nav>

                <div className={styles.modules}>
                    {MODULES.map((m) => {
                        const Icon = m.icon;
                        return (
                            <section key={m.id} id={m.id} className={styles.module}>
                                <div className={styles.copy}>
                                    <span className={styles.modIcon}><Icon size={18} /></span>
                                    <h2>{m.title}</h2>
                                    <p className={styles.lead}>{m.text}</p>
                                    <ul className={styles.points}>
                                        {m.points.map((p) => <li key={p}><CheckCircle2 size={15} /> <span>{p}</span></li>)}
                                    </ul>
                                    <span className={styles.boost}><TrendingUp size={13} /> Sales boost: {m.boost}</span>
                                </div>
                                <motion.div
                                    className={styles.visual}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.4 }}
                                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <Panel panel={m.panel} />
                                </motion.div>
                            </section>
                        );
                    })}
                    <div className={styles.endCta}>
                        <Button to="/demo" size="lg" icon={ArrowUpRight}>Book a free demo</Button>
                    </div>
                </div>
            </div>

            <CTABand />
        </>
    );
}
